import type { DeepReadonly } from "vue";
import type { FormFilterName, FullSearchProps, FullSearchResults, SearchFiltersForm } from "@newgenerated/shared/schema";
import { assert, assertIsDefined } from "@utils/assertion";
import { simpleBackgroundWorker } from "@/components/ask-getabstract/utils/backgroundWorker";
import { delay } from "@utils/asyncUtils";
import { startTimer } from "@/components/ask-getabstract/utils/animationUtils";
import { STREAMING_SPEED_UP_FACTOR, STREAMING_TOKEN_PER_SEC, WAITING_INTERVAL_IN_MS } from "@/components/ask-getabstract/utils/store";
import { type ContentTypeDiscriminator, type ContentTypeProps, defaultAmount, type SearchStoreState } from "@/components/search/fullSearchStoreTypes";
import { createStore, type Store } from "@/common/storeUtils";
import { useGaContext } from "@utils/vue-migration/common/gaContext/gaContext";
import { convertToActionableFiltersForm, convertToSummaryFiltersForm, convertToOnlineContentFiltersForm, type FormFilterExtended, toFormFilterExtended } from "@/components/sorting-filtering-paging/ContentSortingFiltering";
import type { FeatureContext } from "@utils/widgetContextUtils";
import { getInitialContentIxStatusState, type IxStatusHandler } from "@/common/ixStatusUtils";
import type { ApiResult } from "@utils/httpClientCore";
import { createQuestion } from "@newgenerated/shared/stubs/createQuestion";
import { getQuestionAnswers } from "@newgenerated/shared/stubs/getQuestionAnswers";
import _escape from "lodash/escape";

export function getFilter(formFilters: DeepReadonly<FormFilterExtended[]>, filterType: FormFilterName): DeepReadonly<FormFilterExtended | null> {
  return formFilters.find((filter) => filter.identifier === filterType) ?? null;
}

function convertToSearchFiltersForm(
  summaryFormFilters: DeepReadonly<FormFilterExtended[]>,
  actionableFormFilters: DeepReadonly<FormFilterExtended[]>,
  verifaiFiltersForm: DeepReadonly<FormFilterExtended[]>,
  searchTerm: string,
  summariesPage: number,
  channelPage: number,
  verifaiPage: number,
): SearchFiltersForm {
  return {
    summarySearchFilters: convertToSummaryFiltersForm(summaryFormFilters),
    actionableLanguageFormFilter: convertToActionableFiltersForm(actionableFormFilters),
    verifaiFiltersForm: convertToOnlineContentFiltersForm(verifaiFiltersForm),
    query: searchTerm,
    summariesPage: summariesPage,
    channelPage: channelPage,
    verifaiPage: verifaiPage,
  };
}

function getContentType(contentTypes: DeepReadonly<ContentTypeProps[]>, discriminator: ContentTypeDiscriminator): DeepReadonly<ContentTypeProps> {
  const contentType = contentTypes.find((c) => c.kind === discriminator);
  assertIsDefined(contentType);
  return contentType;
}

type SearchFunction = (searchFiltersForm: SearchFiltersForm) => Promise<ApiResult<FullSearchResults>>;

export async function executeSearch(searchStore: Store<SearchStoreState>, newSearchTerm: string, searchFunction: SearchFunction, ixStatusHandler: IxStatusHandler, verifaiEnabled: boolean, softReload: boolean = false): Promise<void> {
  const contentTypes = searchStore.sub("contentTypeProps");
  const searchProps = searchStore.sub("searchProps");
  searchStore
    .sub("searchProps")
    .sub("status")
    .set(softReload ? "LOADMORE" : "LOADING");
  searchProps.sub("searchTerm").set(newSearchTerm);
  const summary = getContentType(contentTypes.get(), "SUMMARY");
  const actionable = getContentType(contentTypes.get(), "ACTIONABLE");
  const verifais = verifaiEnabled ? getContentType(contentTypes.get(), "VERIFAI") : null;
  const channel = getContentType(contentTypes.get(), "CHANNEL");
  const apiResult = await searchFunction(
    convertToSearchFiltersForm(
      summary.filterProps.formFilters,
      actionable.filterProps.formFilters,
      verifais !== null ? verifais.filterProps.formFilters : [],
      _escape(searchProps.sub("searchTerm").get()),
      summary.paging.page,
      channel.paging.page,
      verifais !== null ? verifais.paging.page : 0,
    ),
  );
  if (apiResult.kind === "success") {
    const result = apiResult.data;
    ixStatusHandler.updateItems([...result.summaries.map((s) => s.contentId), ...result.actionables.map((a) => a.contentId), ...result.verifais.map((verifai) => verifai.contentId)]);
    const convertedContentTypes = contentTypes
      .get()
      .map((cat): DeepReadonly<ContentTypeProps> => {
        switch (cat.kind) {
          case "CHANNEL":
            return { ...cat, items: result.channels, paging: result.channelPaging };
          case "SUMMARY":
            return {
              ...cat,
              items: result.summaries,
              filterProps: { formFilters: toFormFilterExtended(result.summaryFormFilters), formFilterExtended: cat.filterProps.formFilterExtended },
              paging: result.summariesPaging,
            };
          case "CUSTOMPAGE":
            return { ...cat, items: result.customPages };
          case "ACTIONABLE":
            return {
              ...cat,
              items: result.actionables,
              filterProps: { formFilters: toFormFilterExtended(result.actionableFormFilters), formFilterExtended: cat.filterProps.formFilterExtended },
            };
          case "VERIFAI":
            return {
              ...cat,
              items: result.verifais,
              filterProps: { formFilters: toFormFilterExtended(result.verifaiFormFilters), formFilterExtended: cat.filterProps.formFilterExtended },
              paging: result.verifaiPaging,
            };
        }
      })
      .filter((c) => c.kind !== "VERIFAI" || verifaiEnabled);
    applyQueryParamsToUrl(convertedContentTypes);

    contentTypes.set(convertedContentTypes);
    searchProps.sub("status").set("IDLE");
    searchProps.set({ ...searchProps.get(), params: new URLSearchParams(result.downloadSourceParams.additionalProperties) });
  } else {
    searchProps.sub("status").set("ERROR");
  }
}

export function getActiveFilterValues(formFilters: DeepReadonly<FormFilterExtended[]>, filterType: FormFilterName): readonly string[] {
  const filter = getFilter(formFilters, filterType);
  return filter !== null ? filter.activeValues : [];
}

export function applyQueryParamsToUrl(contentTypeProps: DeepReadonly<ContentTypeProps[]>): void {
  const queryParams = new URLSearchParams(window.location.search);
  contentTypeProps.forEach((contentType) =>
    contentType.filterProps.formFilters.forEach((filter) => {
      const parameterName = (contentType.kind !== "SUMMARY" ? contentType.kind.toLowerCase() : "") + filter.name;
      queryParams.delete(parameterName);
      filter.activeValues.forEach((value) => {
        if (!(filter.identifier === "AUDIO" && filter.activeValues.at(0) === "false") && !(filter.identifier === "MIAB" && filter.activeValues.at(0) === "false")) {
          queryParams.append(parameterName, value);
        }
      });
    }),
  );
  history.replaceState(null, "", "?" + queryParams.toString());
}

export function readQueryParametersFromUrl(formFilters: FormFilterExtended[], contentType: ContentTypeDiscriminator): FormFilterExtended[] {
  const queryParams = new URLSearchParams(window.location.search);
  return formFilters.map((filter) => {
    const filterName = contentType.toLowerCase() + filter.name;
    const values = queryParams.getAll(filterName);
    return {
      ...filter,
      activeValues: values.length > 0 ? values : filter.activeValues,
    };
  });
}

export function createInitialSearchStore(props: FullSearchProps, features: FeatureContext): Store<SearchStoreState> {
  const { t } = useGaContext();
  const convertedSummaryFormFilters = toFormFilterExtended(props.initialSummaryFormFilters);
  const contentTypes: ContentTypeProps[] = [
    {
      kind: "SUMMARY",
      title: t("general:summaries", ""),
      amountToShow: defaultAmount["SUMMARY"],
      items: [],
      filterProps: { formFilters: readQueryParametersFromUrl(convertedSummaryFormFilters, "SUMMARY"), formFilterExtended: false },
      paging: {
        page: 0,
        totalCount: BigInt(0),
      },
    },
    {
      kind: "ACTIONABLE",
      title: t("general:actionables", ""),
      amountToShow: defaultAmount["ACTIONABLE"],
      items: [],
      filterProps: {
        formFilters: readQueryParametersFromUrl(toFormFilterExtended(props.initialActionableFormFilters), "ACTIONABLE"),
        formFilterExtended: false,
      },
      paging: { page: 0, totalCount: BigInt(0) },
    },
    { kind: "CUSTOMPAGE", title: t("customPage:customPages", ""), amountToShow: defaultAmount["CUSTOMPAGE"], items: [], filterProps: { formFilters: [], formFilterExtended: false }, paging: { page: 0, totalCount: BigInt(0) } },
    {
      kind: "CHANNEL",
      title: t("general:channels", ""),
      amountToShow: defaultAmount["CHANNEL"],
      items: [],
      filterProps: { formFilters: [], formFilterExtended: false },
      paging: { page: 0, totalCount: BigInt(0) },
    },
  ];
  if (features.VERIFAI) {
    contentTypes.push({
      kind: "VERIFAI",
      title: t("general:verify"),
      amountToShow: defaultAmount["VERIFAI"],
      items: [],
      filterProps: { formFilters: readQueryParametersFromUrl(toFormFilterExtended(props.initialVerifaiFormFilters), "VERIFAI"), formFilterExtended: false },
      paging: { page: 0, totalCount: BigInt(0) },
    });
  }
  const store = createStore<SearchStoreState>({
    contentTypeProps: contentTypes,
    searchProps: {
      params: new URLSearchParams(),
      status: "LOADING",
      selectedContentType: "ALL",
      searchTerm: "",
    },
    aiState: {
      kind: "NOT_AVAILABLE",
    },
    ixStatus: getInitialContentIxStatusState(),
  });

  if (features.GET_ABSTRACT_AI) {
    store.sub("aiState").set({
      kind: "AVAILABLE",
      displayFullAnswer: false,
      uiState: { kind: "INITIAL" },
    });

    void simpleBackgroundWorker(
      {
        streamingTokensPerSec: STREAMING_TOKEN_PER_SEC,
        streamingSpeedUpFactor: STREAMING_SPEED_UP_FACTOR,
        analyticsEventVariant: "qa_triggered_from_search",
      },
      {
        getInteractions: () => store.sub("searchProps").get(),
        updateUi: (newState) => {
          const aiState = store.sub("aiState").unpackUnion();
          assert(aiState.kind === "AVAILABLE");
          aiState.store.sub("uiState").set(newState);
        },
        initQuestionAnswer: (props) => createQuestion({ body: props }),
        getQuestionAnswers: (uuid) => getQuestionAnswers({ questionUuid: uuid }),
        delay: () => delay(WAITING_INTERVAL_IN_MS),
        isAborted: () => false,
        startTimer,
      },
    );
  }

  return store;
}
